import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { EyeIcon, EyeSlashIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { AppIcon } from '../../icons/appIcons.tsx';

// Type for the BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function Login() {
  const [email, setEmail] = useState(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      return localStorage.getItem('savedEmail') || '';
    }
    return '';
  });
  const [password, setPassword] = useState(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      try {
        return atob(localStorage.getItem('savedPassword') || '');
      } catch {
        return '';
      }
    }
    return '';
  });
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('rememberMe') === 'true';
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fpStep, setFpStep] = useState<1 | 2 | 3 | 4>(1);
  const [fpEmail, setFpEmail] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpMaskedPhone, setFpMaskedPhone] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const autoLoginAttempted = useRef(false);

  // Check if app is already installed and listen for install prompt
  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Navigate to the appropriate page based on user role
  const navigateByRole = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const response = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success && data.user) {
          if (data.user.role === 'admin') {
            navigate('/admin');
          } else if (data.user.role === 'alumni') {
            navigate('/alumni');
          } else {
            navigate('/dashboard');
          }
          return;
        }
      } catch (err) {
        console.error('Error verifying user role:', err);
      }
    }
    navigate('/dashboard');
  };

  // Auto-login when remembered credentials exist (skip if user explicitly signed out)
  useEffect(() => {
    if (autoLoginAttempted.current) return;
    if (localStorage.getItem('loggedOut') === 'true') return;

    const saved = localStorage.getItem('rememberMe') === 'true';
    const savedEmail = localStorage.getItem('savedEmail') || '';
    const savedPass = localStorage.getItem('savedPassword') || '';

    if (saved && savedEmail && savedPass) {
      autoLoginAttempted.current = true;
      let decodedPass: string;
      try {
        decodedPass = atob(savedPass);
      } catch {
        return;
      }

      (async () => {
        setLoading(true);
        try {
          const success = await login(savedEmail, decodedPass);
          if (success) {
            await navigateByRole();
          }
        } catch {
          // Auto-login failed silently, form stays pre-filled for manual attempt
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle install button click
  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS-specific instructions
      setShowInstallModal(true);
    } else if (deferredPrompt) {
      // Trigger the install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Show generic install instructions
      setShowInstallModal(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        // Clear the signed-out flag so auto-login works next time
        localStorage.removeItem('loggedOut');

        // Save or clear remembered credentials
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('savedEmail', email);
          localStorage.setItem('savedPassword', btoa(password));
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('savedEmail');
          localStorage.removeItem('savedPassword');
        }

        await navigateByRole();
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open the forgot password modal
  const openForgotPassword = () => {
    setFpStep(1);
    setFpEmail(email); // Pre-fill with login email if present
    setFpCode('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpMaskedPhone('');
    setFpError('');
    setFpLoading(false);
    setFpShowPassword(false);
    setShowForgotPassword(true);
  };

  // Step 1: Send verification code
  const handleFpSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fpEmail })
      });
      const data = await response.json();
      if (data.success) {
        setFpMaskedPhone(data.maskedPhone);
        setFpStep(2);
      } else {
        setFpError(data.message || 'Failed to send verification code.');
      }
    } catch {
      setFpError('An error occurred. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2: Verify code
  const handleFpVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      const response = await fetch('/api/auth/verify-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fpEmail, code: fpCode })
      });
      const data = await response.json();
      if (data.success) {
        setFpStep(3);
      } else {
        setFpError(data.message || 'Invalid verification code.');
      }
    } catch {
      setFpError('An error occurred. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  // Step 3: Reset password
  const handleFpResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpError('');

    if (fpNewPassword.length < 6) {
      setFpError('Password must be at least 6 characters.');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match.');
      return;
    }

    setFpLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fpEmail, code: fpCode, newPassword: fpNewPassword })
      });
      const data = await response.json();
      if (data.success) {
        setFpStep(4);
      } else {
        setFpError(data.message || 'Failed to reset password.');
      }
    } catch {
      setFpError('An error occurred. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero Image Section */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Hero Image - Full background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/images/horse-sunset.jpg')",
          }}
        />
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Overlay Content */}
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12 w-full">
          <div className="max-w-lg text-center fade-in">
            {/* Main Heading */}
            <h1 className="text-6xl font-bold font-serif text-white mb-4 drop-shadow-2xl leading-tight">
              God's Green Acre
              <br />
              Association
            </h1>
            
            {/* Tagline */}
            <p className="text-2xl text-white/95 mb-8 font-serif italic drop-shadow-lg">
              ...the dream lives on
            </p>
            
            {/* Description */}
            <p className="text-lg text-white/90 mb-4 leading-relaxed max-w-xl mx-auto drop-shadow-md italic">
              "Where girls become friends, friends become family, and family becomes forever"
            </p>
            
            {/* Member's Portal Label */}
            <p className="text-base text-white/80 mb-10 uppercase tracking-widest drop-shadow-md">
              Member's Portal
            </p>
            
            {/* Call to Action Button */}
            <div className="flex justify-center">
              <a
                href="https://ggaa.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-3 bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Return to Home Page
                <svg 
                  className="ml-2 h-5 w-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-white to-ggaa-light">
        <div className="max-w-md w-full">
          {/* Login Card */}
          <div className="card-elevated p-8 slide-in-right">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-ggaa-dark mb-2">Welcome Back</h2>
              <p className="text-gray-600">Sign in to access your account</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  {error}
                </p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-ggaa-primary transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-ggaa-primary focus:ring-ggaa-primary border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button 
                    type="button"
                    onClick={openForgotPassword}
                    className="font-medium text-ggaa-primary hover:text-ggaa-dark transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                Need access?{' '}
                <a href="mailto:admin@ggaa.org" className="font-medium text-ggaa-primary hover:text-ggaa-dark transition-colors">
                  Contact administrator
                </a>
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button 
                  type="button"
                  onClick={() => setShowDonateModal(true)}
                  className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-ggaa-primary hover:bg-ggaa-dark transition-colors shadow-sm"
                >
                  <AppIcon name="donation" className="h-5 w-5 mr-2" />
                  Donate to GGAA
                </button>
                {!isInstalled && (
                  <button 
                    type="button"
                    onClick={handleInstallClick}
                    className="inline-flex items-center justify-center px-6 py-2 border-2 border-ggaa-primary text-sm font-medium rounded-lg text-ggaa-primary bg-white hover:bg-ggaa-light transition-colors shadow-sm"
                  >
                    <DevicePhoneMobileIcon className="h-5 w-5 mr-2" />
                    Install the App
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p>© 2024 God's Green Acre Association. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Donate Modal */}
      {showDonateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-8 shadow-2xl">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-ggaa-dark">
                💝 Support GGAA
              </h3>
              <button
                type="button"
                onClick={() => setShowDonateModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium text-center">
                  🚀 Online donations coming soon!
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Interested in donating? Please contact us:
                </h4>
                
                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary/10 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-ggaa-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Email</p>
                      <a 
                        href="mailto:info@ggaa.org" 
                        className="text-ggaa-primary hover:text-ggaa-dark font-medium"
                      >
                        info@ggaa.org
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary/10 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-ggaa-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Phone</p>
                      <a 
                        href="tel:+13143308954" 
                        className="text-ggaa-primary hover:text-ggaa-dark font-medium"
                      >
                        314-330-8954
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary/10 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-ggaa-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Mailing Address</p>
                      <p className="text-gray-900 font-medium">
                        6588 State Road Highway Y<br />
                        Hillsboro, MO 63050
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center italic">
                  Thank you for your support! Your generosity helps us continue our mission.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowDonateModal(false)}
                className="w-full px-6 py-3 bg-ggaa-primary text-white rounded-lg hover:bg-ggaa-dark transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-8 shadow-2xl">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-ggaa-dark">
                {fpStep === 4 ? 'Password Reset' : 'Forgot Password'}
              </h3>
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ✕
              </button>
            </div>

            {/* Error */}
            {fpError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{fpError}</p>
              </div>
            )}

            {/* Step 1: Enter email */}
            {fpStep === 1 && (
              <form onSubmit={handleFpSendCode} className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email address and we'll send a verification code to the phone number on file.
                </p>
                <div>
                  <label htmlFor="fp-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="fp-email"
                    type="email"
                    required
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all"
                    placeholder="you@example.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={fpLoading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {fpLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2"
                >
                  Back to Sign In
                </button>
              </form>
            )}

            {/* Step 2: Enter verification code */}
            {fpStep === 2 && (
              <form onSubmit={handleFpVerifyCode} className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                  <p className="text-sm text-green-800 text-center">
                    A verification code was sent to <strong>{fpMaskedPhone}</strong>
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code from your text message. The code expires in 10 minutes.
                </p>
                <div>
                  <label htmlFor="fp-code" className="block text-sm font-semibold text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    id="fp-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={fpCode}
                    onChange={(e) => setFpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={fpLoading || fpCode.length !== 6}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {fpLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setFpStep(1); setFpError(''); setFpCode(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2"
                >
                  Resend code
                </button>
              </form>
            )}

            {/* Step 3: Set new password */}
            {fpStep === 3 && (
              <form onSubmit={handleFpResetPassword} className="space-y-4">
                <p className="text-sm text-gray-600 mb-2">
                  Code verified! Enter your new password below.
                </p>
                <div>
                  <label htmlFor="fp-new-password" className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="fp-new-password"
                      type={fpShowPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={fpNewPassword}
                      onChange={(e) => setFpNewPassword(e.target.value)}
                      className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all"
                      placeholder="At least 6 characters"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-ggaa-primary transition-colors"
                      onClick={() => setFpShowPassword(!fpShowPassword)}
                    >
                      {fpShowPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="fp-confirm-password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    id="fp-confirm-password"
                    type={fpShowPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={fpConfirmPassword}
                    onChange={(e) => setFpConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent transition-all"
                    placeholder="Confirm your password"
                  />
                </div>
                {fpNewPassword && fpConfirmPassword && fpNewPassword !== fpConfirmPassword && (
                  <p className="text-sm text-red-500">Passwords do not match.</p>
                )}
                <button
                  type="submit"
                  disabled={fpLoading || fpNewPassword.length < 6 || fpNewPassword !== fpConfirmPassword}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {fpLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            )}

            {/* Step 4: Success */}
            {fpStep === 4 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-gray-900">Password Reset Successfully!</p>
                <p className="text-sm text-gray-600">
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full btn-primary mt-4"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Install App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-8 shadow-2xl">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-ggaa-dark flex items-center">
                <DevicePhoneMobileIcon className="h-7 w-7 mr-2 text-ggaa-primary" />
                Install GGAA Portal
              </h3>
              <button
                type="button"
                onClick={() => setShowInstallModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {isIOS ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 font-medium text-center">
                      📱 iOS Installation Instructions
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        1
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Tap the Share button</p>
                        <p className="text-sm text-gray-600">
                          Look for the share icon (square with arrow) at the bottom of Safari
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        2
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Scroll down and tap "Add to Home Screen"</p>
                        <p className="text-sm text-gray-600">
                          You may need to scroll down in the share menu to find this option
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        3
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Tap "Add" to confirm</p>
                        <p className="text-sm text-gray-600">
                          The GGAA Portal app will appear on your home screen!
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium text-center">
                      📱 Install as an App
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        1
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Open in Chrome browser</p>
                        <p className="text-sm text-gray-600">
                          Make sure you're using Google Chrome for the best experience
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        2
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Tap the menu (⋮) button</p>
                        <p className="text-sm text-gray-600">
                          Look for the three dots in the top-right corner
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-ggaa-primary text-white rounded-full flex items-center justify-center mr-3 font-bold">
                        3
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Select "Install app" or "Add to Home screen"</p>
                        <p className="text-sm text-gray-600">
                          The GGAA Portal app will be added to your device!
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center italic">
                  Once installed, you can access GGAA Portal directly from your home screen!
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowInstallModal(false)}
                className="w-full px-6 py-3 bg-ggaa-primary text-white rounded-lg hover:bg-ggaa-dark transition-colors font-medium"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
